import { type RestSchema, SessionTokenEngine } from "@mountaya/client";
import { askFramingPageForSession, type FramingPageWindow } from "@mountaya/studio/engines";
import { systemClock } from "@mountaya/studio/seams";

import type { SessionTokenReader } from "@mountayaapp/api";

/**
 * FramingPageSession is the hosted embed's session token, renewed by the page that frames it.
 *
 * An organization that sets `require_session_token` has every read of its hosted embed carry a
 * session token, the map's tiles included, and a token lives minutes. Minting one takes the
 * organization's secret key, which only the integrator's server holds, so the embed asks the page
 * that frames it, whose snippet script answers from the integrator's own endpoint. A
 * `SessionTokenEngine` schedules each ask a minute before the last answer's `expires_at`, and asks
 * again where the page did not answer.
 */
export interface FramingPageSession {
  /**
   * firstAnswer resolves once the framing page has answered, or once its first ask has failed, so
   * a read made after it carries the token wherever the page gave one. It never rejects: an
   * unanswered ask leaves the reads to go out with the token they would have carried anyway.
   *
   * It holds the session from the ask until {@link hold} is first called, and hands it over
   * there: an engine nobody holds starts a fresh ask when it is held again, so letting go in
   * between would ask the page twice for one load.
   */
  readonly firstAnswer: () => Promise<void>;
  /**
   * hold keeps the token renewed for as long as it is held, and returns the release. The engine
   * renews only while something holds it, so the surface holds it for its whole life.
   */
  readonly hold: () => () => void;
  /**
   * rereadDue reports whether what the embed read before the framing page's first token is to be
   * read again under it: a read went out before that token, and the token is now in hand and is
   * not the one the address carried. It turns true once, when the token lands, and stays true.
   *
   * It is what lets the embed use the address's token at once and still recover where that token
   * was refused (D58): a read the API refused under it, a catalog or the base map, is never asked
   * again on its own. Where the page answered before any read went out, or the address carried
   * the token the page answers, there is nothing to read again.
   */
  readonly rereadDue: () => boolean;
  /**
   * sessionToken is the token the next request sends: the framing page's latest answer, or,
   * until it has answered, the one the embed's own address carried.
   */
  readonly sessionToken: SessionTokenReader;
  /**
   * subscribe calls the listener after every change of the session's token, and returns the
   * release. It holds the session as {@link hold} does, so a subscriber is also renewing it.
   * Stable, so it is `useSyncExternalStore`'s first argument as it is, with {@link rereadDue}.
   */
  readonly subscribe: (listener: () => void) => () => void;
}

/** HoldFramingPageSessionOptions is what {@link holdFramingPageSession} takes. */
export interface HoldFramingPageSessionOptions {
  /** One ask of the framing page, as `askFramingPageForSession` makes it. */
  readonly ask: () => Promise<RestSchema.Session>;
  /** The token the embed's address carried, sent until the framing page answers one. */
  readonly initialToken: string | undefined;
}

/**
 * holdFramingPageSession builds the hosted embed's renewing session token over one ask of the
 * framing page.
 *
 * The engine asks as soon as something holds it, even where the address carried a token: that
 * token's expiry is not known here, and the first answer is what the renewals are scheduled from.
 * While the page has not answered, the address's token is the one sent, and every read that goes
 * out with it is noted, so the embed can read again once the page's token lands (rereadDue).
 *
 * @param options - The ask, and the token the address carried.
 */
export const holdFramingPageSession = ({
  ask,
  initialToken,
}: HoldFramingPageSessionOptions): FramingPageSession => {
  const engine = new SessionTokenEngine({ mint: ask });
  // The hold firstAnswer takes, until the surface's own hold takes over from it.
  let awaiting: (() => void) | null = null;
  // Whether the page's first token has been seen, whether a read went out before it, and the
  // latch rereadDue answers from. A read is noted only before that first token: a renewal that
  // fails later keeps the token in hand, and is no reason to read everything again.
  let answered = false;
  let readBeforeAnswer = false;
  let rereadDue = false;

  const tokenNow = (): string | undefined => {
    const token = engine.getSnapshot().token;
    if (token !== undefined && !answered) {
      answered = true;
      rereadDue = readBeforeAnswer && token !== initialToken;
    }

    return token;
  };

  return {
    firstAnswer: () =>
      new Promise<void>((resolve) => {
        let settled = false;
        const check = () => {
          const snapshot = engine.getSnapshot();
          if (settled || (snapshot.token === undefined && snapshot.error === undefined)) {
            return;
          }

          settled = true;
          resolve();
        };

        const release = engine.subscribe(check);
        const previous = awaiting;
        awaiting =
          previous === null
            ? release
            : () => {
                previous();
                release();
              };
        check();
      }),
    hold: () => {
      const release = engine.subscribe(() => {});
      awaiting?.();
      awaiting = null;

      return release;
    },
    rereadDue: () => {
      tokenNow();

      return rereadDue;
    },
    sessionToken: () => {
      const token = tokenNow();
      if (token !== undefined) {
        return token;
      }

      if (!answered) {
        readBeforeAnswer = true;
      }

      return initialToken;
    },
    subscribe: (listener) => engine.subscribe(listener),
  };
};

/**
 * askFramingPageOf is the ask {@link holdFramingPageSession} makes in a page: the embed's own
 * window asks its parent, on the system clock.
 *
 * @param embed - The embed's own window.
 */
export const askFramingPageOf =
  (embed: FramingPageWindow): (() => Promise<RestSchema.Session>) =>
  () =>
    askFramingPageForSession({ clock: systemClock(), embed });

/**
 * isFramed reports whether the embed's window has a page above it to ask. A top-level window is
 * its own parent, and an ask there would reach only itself.
 *
 * @param embed - The embed's own window.
 */
export const isFramed = (embed: Pick<FramingPageWindow, "parent">): boolean =>
  !Object.is(embed.parent, embed);
